import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent, EventType, EventCategory, EventMetadata } from '@/lib/analytics';

const SESSION_ID_KEY = 'user_session_id';

const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Hook for tracking user behavior events
 * Automatically injects session_id and user_id
 */
export const useTracking = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [userId, setUserId] = useState<string | undefined>(undefined);

  // Initialize session ID
  useEffect(() => {
    let storedSessionId = localStorage.getItem(SESSION_ID_KEY);
    
    if (!storedSessionId) {
      storedSessionId = generateSessionId();
      localStorage.setItem(SESSION_ID_KEY, storedSessionId);
    }
    
    setSessionId(storedSessionId);
  }, []);

  // Track auth state for user_id
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUserId(session?.user?.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Track a user interaction event
   */
  const track = useCallback((
    type: EventType,
    options?: {
      productId?: string;
      category?: EventCategory;
      metadata?: EventMetadata;
    }
  ) => {
    if (!sessionId) return;

    trackEvent({
      type,
      productId: options?.productId,
      category: options?.category,
      metadata: options?.metadata,
      sessionId,
      userId,
    });
  }, [sessionId, userId]);

  // Convenience methods for common events
  const trackAddToCart = useCallback((productId: string, metadata?: { quantity?: number; source?: string }) => {
    track('add_to_cart', { productId, category: 'conversion', metadata });
  }, [track]);

  const trackRemoveFromCart = useCallback((productId: string, metadata?: { quantity?: number }) => {
    track('remove_from_cart', { productId, category: 'conversion', metadata });
  }, [track]);

  const trackUpdateCartQuantity = useCallback((productId: string, oldQuantity: number, newQuantity: number) => {
    track('update_cart_quantity', { 
      productId, 
      category: 'conversion', 
      metadata: { old_quantity: oldQuantity, new_quantity: newQuantity } 
    });
  }, [track]);

  const trackCheckoutStarted = useCallback((metadata: { cart_value: number; item_count: number }) => {
    track('checkout_started', { category: 'conversion', metadata });
  }, [track]);

  const trackCheckoutCompleted = useCallback((metadata: { order_id: string; order_value: number }) => {
    track('checkout_completed', { category: 'conversion', metadata });
  }, [track]);

  const trackFavoriteAdded = useCallback((productId: string) => {
    track('favorite_added', { productId, category: 'engagement' });
  }, [track]);

  const trackFavoriteRemoved = useCallback((productId: string) => {
    track('favorite_removed', { productId, category: 'engagement' });
  }, [track]);

  const trackChatbotOpened = useCallback((trigger?: string) => {
    track('chatbot_opened', { category: 'engagement', metadata: { trigger } });
  }, [track]);

  const trackChatbotMessageSent = useCallback((messageLength: number) => {
    track('chatbot_message_sent', { category: 'engagement', metadata: { message_length: messageLength } });
  }, [track]);

  const trackChatbotFeedback = useCallback((feedbackType: 'up' | 'down') => {
    track('chatbot_feedback', { category: 'engagement', metadata: { feedback_type: feedbackType } });
  }, [track]);

  const trackQuickActionClicked = useCallback((label: string, question: string) => {
    track('quick_action_clicked', { category: 'engagement', metadata: { action_label: label, action_question: question } });
  }, [track]);

  const trackCategoryFilterApplied = useCallback((categoryName: string) => {
    track('category_filter_applied', { category: 'navigation', metadata: { category_name: categoryName } });
  }, [track]);

  const trackTestMethodFilterApplied = useCallback((methodName: string) => {
    track('test_method_filter_applied', { category: 'navigation', metadata: { method_name: methodName } });
  }, [track]);

  const trackSortChanged = useCallback((sortOption: string) => {
    track('sort_changed', { category: 'navigation', metadata: { sort_option: sortOption } });
  }, [track]);

  const trackSearchPerformed = useCallback((query: string, resultsCount: number) => {
    track('search_performed', { category: 'discovery', metadata: { query, results_count: resultsCount } });
  }, [track]);

  const trackSearchResultClicked = useCallback((productId: string, resultPosition: number) => {
    track('search_result_clicked', { productId, category: 'discovery', metadata: { result_position: resultPosition } });
  }, [track]);

  const trackUpsellViewed = useCallback((currentProduct: string, upgradeProduct: string) => {
    track('upsell_viewed', { category: 'discovery', metadata: { current_product: currentProduct, upgrade_product: upgradeProduct } });
  }, [track]);

  const trackUpsellClicked = useCallback((upgradeProductId: string, action: 'view' | 'add') => {
    track('upsell_clicked', { productId: upgradeProductId, category: 'discovery', metadata: { action } });
  }, [track]);

  const trackCtaClicked = useCallback((buttonLabel: string, pageContext?: string) => {
    track('cta_clicked', { category: 'ui_interaction', metadata: { button_label: buttonLabel, page_context: pageContext } });
  }, [track]);

  const trackCartSidebarOpened = useCallback((trigger?: string) => {
    track('cart_sidebar_opened', { category: 'ui_interaction', metadata: { trigger } });
  }, [track]);

  const trackSupportDialogOpened = useCallback((sourcePage?: string) => {
    track('support_dialog_opened', { category: 'ui_interaction', metadata: { page_context: sourcePage } });
  }, [track]);

  return {
    track,
    trackAddToCart,
    trackRemoveFromCart,
    trackUpdateCartQuantity,
    trackCheckoutStarted,
    trackCheckoutCompleted,
    trackFavoriteAdded,
    trackFavoriteRemoved,
    trackChatbotOpened,
    trackChatbotMessageSent,
    trackChatbotFeedback,
    trackQuickActionClicked,
    trackCategoryFilterApplied,
    trackTestMethodFilterApplied,
    trackSortChanged,
    trackSearchPerformed,
    trackSearchResultClicked,
    trackUpsellViewed,
    trackUpsellClicked,
    trackCtaClicked,
    trackCartSidebarOpened,
    trackSupportDialogOpened,
  };
};
